import { ApiError } from "../api/client";

// Traduce cualquier error a un aviso que el vendedor pueda entender y actuar.
// Los mensajes que ya escribimos nosotros (validaciones, `body.error` de n8n
// y del backend) se respetan tal cual: dicen qué pasó con sus datos. Lo que
// se reemplaza es el texto técnico del navegador ("Failed to fetch",
// "signal timed out", "Unexpected token <…") y los códigos HTTP sin cuerpo.

export type Aviso = { titulo: string; mensaje: string };

const PORCODIGO: Record<number, Aviso> = {
  400: {
    titulo: "Revisa los datos",
    mensaje:
      "Algún dato no tiene el formato esperado. Corrígelo e inténtalo de nuevo.",
  },
  401: {
    titulo: "Tu sesión no es válida",
    mensaje: "Vuelve a entrar con tu nombre y PIN para continuar.",
  },
  403: {
    titulo: "Sin permiso",
    mensaje:
      "Tu usuario no tiene permiso para hacer esto. Pide ayuda a un administrador.",
  },
  404: {
    titulo: "No encontrado",
    mensaje:
      "Lo que buscas ya no existe o fue movido. Recarga e inténtalo de nuevo.",
  },
  409: {
    titulo: "Hay un conflicto",
    mensaje:
      "Alguien más cambió este registro. Recarga para ver la versión actual.",
  },
  413: {
    titulo: "Archivo demasiado grande",
    mensaje: "Reduce el tamaño del archivo e inténtalo de nuevo.",
  },
  429: {
    titulo: "Demasiados intentos",
    mensaje: "Espera un momento antes de volver a intentarlo.",
  },
};

const SERVIDOR: Aviso = {
  titulo: "El servicio tuvo un problema",
  mensaje:
    "No es un error tuyo. Inténtalo de nuevo en unos segundos; si continúa, avisa a soporte.",
};
const SIN_CONEXION: Aviso = {
  titulo: "Sin conexión con el servicio",
  mensaje:
    "Revisa tu conexión a internet e inténtalo de nuevo. Tus datos siguen en el formulario.",
};
const TIEMPO: Aviso = {
  titulo: "El servicio tardó demasiado",
  mensaje:
    "La operación no respondió a tiempo. Inténtalo de nuevo en unos segundos.",
};
const RESPUESTA_INVALIDA: Aviso = {
  titulo: "Respuesta inesperada",
  mensaje:
    "El servicio respondió algo que no pudimos leer. Inténtalo de nuevo.",
};
const GENERICO: Aviso = {
  titulo: "Algo salió mal",
  mensaje: "No se pudo completar la acción. Inténtalo de nuevo.",
};

// Textos que genera el navegador o el runtime, nunca nosotros.
const TECNICO =
  /failed to fetch|networkerror|load failed|network request failed|fetch failed|signal timed out|timed? ?out|aborted|unexpected token|json|is not valid|undefined|null|cannot read|is not a function|syntaxerror|typeerror/i;

// El mensaje genérico que arma `request()` cuando la respuesta no trae
// `error`: el código HTTP no le sirve al vendedor.
const GENERICO_HTTP = /^El servicio respondió con un error \(\d+\)\.$/;

export function explicarError(error: unknown): Aviso {
  if (error == null || error === "") return GENERICO;
  const nombre = error instanceof Error ? error.name : "";
  const texto = (error instanceof Error ? error.message : String(error)).trim();

  if (nombre === "TimeoutError") return TIEMPO;
  if (nombre === "AbortError") return TIEMPO;
  if (!navegadorEnLinea()) return SIN_CONEXION;

  if (error instanceof ApiError) {
    const base =
      error.status >= 500 ? SERVIDOR : (PORCODIGO[error.status] ?? GENERICO);
    // El backend y n8n responden `{ error: "..." }` en español para el
    // vendedor (ej. "Ya existe la cotización GYE-…"): ese texto manda.
    return texto && !GENERICO_HTTP.test(texto) && !TECNICO.test(texto)
      ? { titulo: base.titulo, mensaje: texto }
      : base;
  }
  if (error instanceof TypeError && /fetch|network|load failed/i.test(texto))
    return SIN_CONEXION;
  if (error instanceof SyntaxError) return RESPUESTA_INVALIDA;
  if (/timed? ?out|signal timed out/i.test(texto)) return TIEMPO;
  if (
    /failed to fetch|networkerror|load failed|network request failed/i.test(
      texto,
    )
  )
    return SIN_CONEXION;
  if (!texto || TECNICO.test(texto)) return GENERICO;
  // Mensajes propios: o describen una falla del servicio ("El servicio no
  // devolvió un PDF.") o una validación de lo que escribió el vendedor
  // ("Elige un contacto…", "El PIN no es correcto.").
  return {
    titulo: FALLA_SERVICIO.test(texto)
      ? "No se pudo completar"
      : "Revisa los datos",
    mensaje: texto,
  };
}

const FALLA_SERVICIO =
  /^(el servicio|no se pudo|no se pudieron|no se recibió|no se confirmó)|no está disponible|no fue confirmad/i;

// Reemplaza la burbuja nativa de validación ("Completa este campo."), que
// sale con el estilo y el idioma del navegador y no dice cuál campo falla.
export type CampoInvalido = {
  etiqueta: string;
  etiquetaHtml: string; // "select", "input", "textarea"
  tipo: string;
  min?: string;
  max?: string;
  ayuda?: string; // atributo `title`: explica el formato esperado
  validity: Pick<
    ValidityState,
    | "valueMissing"
    | "typeMismatch"
    | "patternMismatch"
    | "rangeUnderflow"
    | "rangeOverflow"
    | "stepMismatch"
    | "tooShort"
    | "tooLong"
    | "badInput"
  >;
  mensajeNativo?: string;
};

export function mensajeDeCampo(c: CampoInvalido): Aviso {
  const q = `«${c.etiqueta || "este campo"}»`,
    v = c.validity;
  if (v.valueMissing)
    return {
      titulo: "Falta un dato",
      mensaje:
        c.etiquetaHtml === "select"
          ? `Elige una opción en ${q}.`
          : c.tipo === "checkbox"
            ? `Marca ${q} para continuar.`
            : c.tipo === "file"
              ? `Adjunta un archivo en ${q}.`
              : `Completa ${q}.`,
    };
  if (v.typeMismatch && c.tipo === "email")
    return {
      titulo: "Correo no válido",
      mensaje: `Escribe un correo válido en ${q} (ej. nombre@empresa.com).`,
    };
  if (v.badInput || (v.typeMismatch && c.tipo === "number"))
    return { titulo: "Dato no válido", mensaje: `${q} debe ser un número.` };
  if (v.patternMismatch)
    return {
      titulo: "Formato no válido",
      mensaje: c.ayuda
        ? `${q}: ${c.ayuda}.`
        : `${q} no tiene el formato esperado.`,
    };
  if (v.rangeUnderflow)
    return {
      titulo: "Valor muy bajo",
      mensaje: `${q} debe ser ${c.min} o más.`,
    };
  if (v.rangeOverflow)
    return {
      titulo: "Valor muy alto",
      mensaje: `${q} debe ser ${c.max} o menos.`,
    };
  if (v.stepMismatch)
    return {
      titulo: "Dato no válido",
      mensaje: `${q} no admite ese valor. Revisa los decimales.`,
    };
  if (v.tooShort || v.tooLong)
    return {
      titulo: "Largo no válido",
      mensaje: `Revisa la cantidad de caracteres de ${q}.`,
    };
  return {
    titulo: "Revisa este dato",
    mensaje: c.mensajeNativo || `${q} tiene un valor no válido.`,
  };
}

function navegadorEnLinea() {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}
